import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth, can, ROLE } from '../lib/auth';
import { RecordPeek, PeekLink, type PeekTarget } from '../components/RecordPeek';

/* ── types ─────────────────────────────────────────────────── */

interface Machine { id: string; name: string; displayName: string }
interface PlannerJob {
  jobId: string; poRefNumber: string;
  client: string; productName: string; jobName: string;
  totalBoxes: string; totalSheets: string; topFinish: string;
  colorsFront1: string; colorsFront2: string; colorsBack: string;
  machine: string; machineName: string; passNo: number;
  // True when this row represents the BACK-side pass (MachineBack) rather
  // than a front pass — used for the "Back" badge and to pick ColorsBack.
  isBack: boolean;
  promiseDate: string | null; jobStatus: string;
  boardStatus: string; boardExpectedDate: string | null;
  designNo: string; ups: string; orderType: string;
  createdOn: string;
  coating: string; custSize: string; paperType: string; gsm: string;
  poDate: string | null;
  // Client-requested quantity — seeds the Actual vs Wastage split so the
  // planner can show both numbers alongside the stored TotalSheet_withWastage.
  clientQty: string;
  totalPasses: number; pass1Machine: string; pass2Machine: string; pass3Machine: string;
  isReissued: boolean;
  /** How many times the card was reissued, and the latest reason. */
  reissueCount?: number; reissueReason?: string;
}
interface PlannerData { machines: Machine[]; jobs: PlannerJob[] }

interface PlanEntry {
  planId: number; planDate: string; machineId: string;
  jobId: string; passNo: number; sequence: number;
  plannedQty: number; actualQty: number | null; wastageQty: number | null;
  status: string;
  client: string; productName: string; jobName: string;
  totalBoxes: string; totalSheets: string;
  designNo: string; ups: string; topFinish: string;
  colorsFront1: string; colorsFront2: string; colorsBack: string;
  promiseDate: string | null; orderType: string; machineName: string;
  boardStatus: string; boardExpectedDate: string | null;
  coating: string; custSize: string; paperType: string; gsm: string;
  poDate: string | null;
  totalPasses: number; pass1Machine: string; pass2Machine: string; pass3Machine?: string;
  isBack?: boolean;
  isReissued: boolean;
  /** How many times the card was reissued, and the latest reason. */
  reissueCount?: number; reissueReason?: string;
  /** The job card's client quantity: with Ups, splits its sheets into actual + wastage. */
  clientQty?: string;
}

interface PendingActual { date: string; count: number }

/** dataTransfer type of an Available Jobs row being moved: its row key. */
const JOB_ROW_DRAG = 'application/x-planner-jobs-key';
/** dataTransfer type of a column header being moved — rows ignore it. */
const COL_DRAG = 'application/x-planner-col';

/**
 * One row of Available Jobs: a pass of a job.  A job printed on both sides
 * (or twice on the front) has a row per pass, so the job number alone does
 * not tell its rows apart.
 */
const rowKey = (j: { jobId: string; passNo: number }) => `${j.jobId}|${j.passNo}`;

/* ── Available Jobs columns ────────────────────────────────── */

/**
 * The Available Jobs table, Excel-style: drag a header to move its column,
 * drag a header's right edge to size it (double-click the edge for the usual
 * width).  Each browser keeps its own layout.
 */
interface JobCol {
  id: string;
  label: string;
  /** Usual width, px. */
  width: number;
  align?: 'left' | 'center' | 'right';
  /** Long text wraps onto more lines; otherwise it is cut short with … */
  wrap?: boolean;
  /** Extra cell classes. */
  cls?: string;
  title?: string;
}
const JOB_COLS: JobCol[] = [
  { id: 'job', label: 'Job', width: 130, wrap: true, cls: 'font-mono' },
  { id: 'history', label: 'History', width: 90, align: 'center' },
  { id: 'client', label: 'Client', width: 150 },
  { id: 'product', label: 'Product', width: 210, wrap: true },
  { id: 'design', label: 'Design', width: 110, cls: 'text-slate-600' },
  { id: 'ups', label: 'UPS', width: 50, align: 'center' },
  { id: 'colors', label: 'Colors', width: 170, wrap: true },
  { id: 'topFinish', label: 'Top Finish', width: 130, wrap: true, cls: 'text-slate-600' },
  { id: 'size', label: 'Size', width: 120, cls: 'text-slate-600' },
  { id: 'board', label: 'Board', width: 120, cls: 'text-slate-600' },
  { id: 'gsm', label: 'GSM', width: 55, align: 'center' },
  { id: 'poQty', label: 'PO Qty', width: 80, align: 'right', cls: 'font-medium' },
  { id: 'actual', label: 'Actual', width: 75, align: 'right', title: 'Actual sheets (Client Qty ÷ Ups, rounded up)' },
  { id: 'sheets', label: 'Sheets (+W)', width: 110, align: 'right', cls: 'font-semibold', title: "Total sheets including wastage — what's stored on the job card" },
  { id: 'dod', label: 'DOD', width: 125 },
  { id: 'poDate', label: 'PO Date', width: 100, cls: 'text-slate-600' },
  { id: 'boardStatus', label: 'Board Status', width: 130 },
];
const JOB_COLS_KEY = 'planner-jobs-columns';
const MIN_COL_WIDTH = 40;
/** The + / ⇄ column — always first, always this wide. */
const ACTION_COL_WIDTH = 64;

/* ── helpers ───────────────────────────────────────────────── */

function fmtDate(s: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function toISO(d: Date) { return d.toISOString().slice(0, 10); }

function dodDays(p: string | null): number | null {
  if (!p) return null;
  const d = new Date(p);
  if (isNaN(d.getTime())) return null;
  const today = new Date(new Date().toDateString());
  return Math.ceil((d.getTime() - today.getTime()) / 86400000);
}

function colorCount(f1: string, f2: string, back: string): number {
  let n = 0;
  for (const s of [f1, f2, back]) if (s) n += s.split(' + ').length;
  return n;
}

function allColors(f1: string, f2: string, back: string): string {
  return [f1, f2, back].filter(Boolean).join(' + ');
}

/**
 * Colors this specific queue row's pass actually lays down on the sheet.
 * Front / Front2 / Back are always separate lines of work, so each row shows
 * ONLY its own pass — never a mash-up of front + back on the same row.
 *   – isBack row: ColorsBack
 *   – passNo === 2 (front): ColorsFront2
 *   – everything else: ColorsFront1
 */
function passColors(f1: string, f2: string, back: string, passNo: number, _totalPasses: number, isBack: boolean): string {
  if (isBack) return back || '';
  if (passNo === 2) return f2 || '';
  return f1 || '';
}

/**
 * Split TotalSheet_withWastage back into Actual Sheets + Wastage.  Same rule
 * the job-card form uses: base = ceil(clientQty / ups), wastage = total - base.
 * Returns blanks when we cannot derive a base without lying (missing qty/ups,
 * or a base bigger than the stored total).
 */
function splitSheets(clientQty: string, ups: string, totalWithWastage: string): { base: string; wastage: string } {
  const q = Number(clientQty) || 0;
  const u = Number(ups) || 0;
  const total = Number(totalWithWastage) || 0;
  if (q <= 0 || u <= 0 || total <= 0) return { base: '', wastage: '' };
  const base = Math.max(0, Math.ceil(q / u));
  if (base > total) return { base: '', wastage: '' };
  return { base: String(base), wastage: String(total - base) };
}

/** The job card's actual sheets and wastage for a plan row that plans the card's full sheets. */
function cardSplit(e: { clientQty?: string; ups: string; totalSheets: string; plannedQty: number }): { base: number; wastage: number } | null {
  const s = splitSheets(e.clientQty ?? '', e.ups, e.totalSheets);
  if (!s.base) return null;
  const base = Number(s.base), wastage = Number(s.wastage);
  return base + wastage === e.plannedQty ? { base, wastage } : null;
}

/**
 * "History" as the planner reads it — whether this job has been done
 * before (Repeat) or is fresh (New/Change/Unconfirmed).  It is NOT the
 * job's calendar age; a Repeat can be created today and it still counts
 * as a job WITH history because the product has been printed before.
 */
function jobHistoryChip(orderType: string): { label: string; tone: string } | null {
  const t = (orderType || '').trim().toLowerCase();
  if (!t) return null;
  if (t === 'repeat') return { label: 'Repeat', tone: 'bg-emerald-100 text-emerald-700' };
  if (t === 'new')    return { label: 'NEW',    tone: 'bg-amber-100 text-amber-700 font-bold' };
  if (t === 'change') return { label: 'Change', tone: 'bg-purple-100 text-purple-700' };
  if (t === 'unconfirmed') return { label: 'Unconfirmed', tone: 'bg-slate-100 text-slate-600' };
  // Anything else the shop punches in — surface it as-is rather than swallow it.
  return { label: orderType, tone: 'bg-slate-100 text-slate-600' };
}

function downloadPlanCSV(plans: PlanEntry[], machine: string, date: string) {
  const header = ['#', 'Job No', 'Client', 'Product', 'Design', 'UPS', 'Colors', 'Top Finish', 'Size', 'Board', 'GSM', 'PO Qty', 'Sheets', 'DOD', 'PO Date', 'Planned', 'Actual', 'Wastage', 'Status', 'Reissued'];
  const rows = plans.map((p, i) => [
    i + 1, p.jobId, p.client, p.productName || p.jobName, p.designNo, p.ups,
    allColors(p.colorsFront1, p.colorsFront2, p.colorsBack),
    p.topFinish, p.custSize, p.paperType, p.gsm, p.totalBoxes, p.totalSheets,
    p.promiseDate ?? '', p.poDate ?? '', p.plannedQty, p.actualQty ?? '', p.wastageQty ?? '', p.status, p.isReissued ? 'Yes' : '',
  ]);
  const csv = [header, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Plan_${machine}_${date}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* Hold a value still for `ms` after the last change. Used to keep the search
 * box from re-filtering + re-sorting the whole row set on every keystroke. */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

interface ColLayout { order: string[]; widths: Record<string, number> }

/** The saved order, cleaned: columns that still exist, and new ones at their usual place. */
function colOrder(l: ColLayout): string[] {
  const ids = l.order.filter((id, i) => JOB_COLS.some((c) => c.id === id) && l.order.indexOf(id) === i);
  JOB_COLS.forEach((c, i) => { if (!ids.includes(c.id)) ids.splice(Math.min(i, ids.length), 0, c.id); });
  return ids;
}

/** The Available Jobs columns as this browser has them: order and widths. */
function useJobColumns() {
  const [layout, setLayout] = useState<ColLayout>(() => {
    try {
      const v = JSON.parse(localStorage.getItem(JOB_COLS_KEY) || 'null');
      if (v && Array.isArray(v.order) && v.widths && typeof v.widths === 'object') return v as ColLayout;
    } catch { /* nothing saved */ }
    return { order: [], widths: {} };
  });
  useEffect(() => {
    try { localStorage.setItem(JOB_COLS_KEY, JSON.stringify(layout)); } catch { /* storage full — ignore */ }
  }, [layout]);

  const cols = useMemo(() => colOrder(layout).map((id) => {
    const c = JOB_COLS.find((x) => x.id === id)!;
    return { ...c, width: Math.max(MIN_COL_WIDTH, layout.widths[id] ?? c.width) };
  }), [layout]);
  const customized = Object.keys(layout.widths).length > 0
    || colOrder(layout).some((id, i) => JOB_COLS[i]?.id !== id);

  /** Put column `id` before (or after) column `target`. */
  const move = useCallback((id: string, target: string, after: boolean) => setLayout((l) => {
    const ids = colOrder(l).filter((x) => x !== id);
    const at = ids.indexOf(target);
    if (at < 0) return l;
    ids.splice(after ? at + 1 : at, 0, id);
    return { ...l, order: ids };
  }), []);
  const resize = useCallback((id: string, width: number) => setLayout((l) => (
    { ...l, widths: { ...l.widths, [id]: Math.round(Math.max(MIN_COL_WIDTH, width)) } }
  )), []);
  const defaultWidth = useCallback((id: string) => setLayout((l) => {
    const widths = { ...l.widths };
    delete widths[id];
    return { ...l, widths };
  }), []);
  const reset = useCallback(() => setLayout({ order: [], widths: {} }), []);
  return { cols, customized, move, resize, defaultWidth, reset };
}

/**
 * A header cell: its label drags the column to another place (a line shows
 * which side of a header it lands on); its right edge drags to size it.
 */
function ColHeader({ col, onMove, onResize, onDefaultWidth }: {
  col: JobCol;
  onMove: (id: string, target: string, after: boolean) => void;
  onResize: (id: string, width: number) => void;
  onDefaultWidth: (id: string) => void;
}) {
  const [over, setOver] = useState<'before' | 'after' | null>(null);

  function startResize(e: React.PointerEvent<HTMLSpanElement>) {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = col.width;
    let lastX = startX;
    let frame = 0;
    // One width a frame while dragging; letting go always lands the last one.
    const move = (ev: PointerEvent) => {
      lastX = ev.clientX;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => onResize(col.id, startW + lastX - startX));
    };
    const up = () => {
      cancelAnimationFrame(frame);
      if (lastX !== startX) onResize(col.id, startW + lastX - startX);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.cursor = '';
    };
    document.body.style.cursor = 'col-resize';
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  return (
    <th
      className={`relative px-2 py-2 font-medium text-slate-600 select-none ${
        col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
      }`}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes(COL_DRAG)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const r = e.currentTarget.getBoundingClientRect();
        const side = e.clientX < r.left + r.width / 2 ? 'before' : 'after';
        if (over !== side) setOver(side);
      }}
      onDragLeave={() => setOver(null)}
      onDrop={(e) => {
        const id = e.dataTransfer.getData(COL_DRAG);
        const side = over;
        setOver(null);
        if (!id) return;
        e.preventDefault();
        if (id !== col.id) onMove(id, col.id, side === 'after');
      }}
    >
      {over && <span className={`pointer-events-none absolute inset-y-0 w-0.5 bg-brand ${over === 'before' ? 'left-0' : 'right-0'}`} />}
      <span
        draggable
        className="block truncate cursor-grab active:cursor-grabbing"
        title={`${col.title ? `${col.title} — ` : ''}drag to move this column`}
        onDragStart={(e) => {
          e.stopPropagation();
          e.dataTransfer.setData(COL_DRAG, col.id);
          e.dataTransfer.effectAllowed = 'move';
        }}
      >
        {col.label}
      </span>
      <span
        role="separator"
        aria-orientation="vertical"
        title="Drag to size · double-click for the usual width"
        className="absolute right-0 top-0 z-10 h-full w-2 cursor-col-resize hover:bg-brand/30"
        onPointerDown={startResize}
        onDoubleClick={() => onDefaultWidth(col.id)}
      />
    </th>
  );
}

/** What each Available Jobs row works out once, for its cells. */
interface RowCtx {
  days: number | null;
  urgent: boolean;
  hist: { label: string; tone: string } | null;
  sheetSplit: { base: string; wastage: string };
  passChain: string;
}

/** One Available Jobs cell.  Plain text comes back as a string (the cell's tooltip too). */
function jobCell(id: string, j: PlannerJob, x: RowCtx, onPeek: (t: PeekTarget) => void): ReactNode {
  switch (id) {
    case 'job':
      return (
        <div className="flex flex-wrap items-center gap-1">
          <PeekLink className="text-[11px]" title="View job card" onOpen={() => onPeek({ kind: 'job', id: j.jobId })}>
            {j.jobId.split('/').pop()}
          </PeekLink>
          {j.isBack
            ? <span className="px-1 py-0.5 rounded bg-sky-100 text-sky-700 text-[10px] font-medium" title="Back-side pass — prints on MachineBack">Back</span>
            : j.passNo === 2
              ? <span className="px-1 py-0.5 rounded bg-purple-100 text-purple-700 text-[10px] font-medium">2nd</span>
              : null}
          {j.totalPasses >= 2 && (
            <span
              title={`${j.totalPasses}-pass job — ${x.passChain || 'machine chain unknown'}`}
              className={`px-1 py-0.5 rounded text-[10px] font-semibold ${
                j.totalPasses === 3 ? 'bg-rose-100 text-rose-700' : 'bg-orange-100 text-orange-700'
              }`}
            >
              {j.totalPasses}P{x.passChain ? ` ${x.passChain}` : ''}
            </span>
          )}
          {j.isReissued && (
            <span className="px-1 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-semibold" title={`Reissued${(j.reissueCount ?? 0) > 1 ? ` ${j.reissueCount} times` : ''}: ${j.reissueReason || ''}`}>
              R{(j.reissueCount ?? 0) > 1 ? j.reissueCount : ''}
            </span>
          )}
        </div>
      );
    case 'history':
      return (
        <div className="flex flex-col items-center gap-0.5">
          {j.isReissued && (
            <>
              <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-bold whitespace-nowrap">
                REISSUE{(j.reissueCount ?? 0) > 1 ? ` ×${j.reissueCount}` : ''}
              </span>
              {j.reissueReason && <span className="text-[10px] leading-tight text-red-700 whitespace-normal">{j.reissueReason}</span>}
            </>
          )}
          {x.hist
            ? <span className={`px-1.5 py-0.5 rounded text-[10px] whitespace-nowrap ${x.hist.tone}`}>{x.hist.label}</span>
            : !j.isReissued && <span className="text-slate-300">—</span>}
        </div>
      );
    case 'client': return j.client;
    case 'product': return j.productName || j.jobName;
    case 'design': return j.designNo || '—';
    case 'ups': return j.ups || '—';
    case 'colors': {
      const c = passColors(j.colorsFront1, j.colorsFront2, j.colorsBack, j.passNo, j.totalPasses, j.isBack);
      return c ? <span className="text-[10px] text-slate-600">{c}</span> : '—';
    }
    case 'topFinish': return j.topFinish || '—';
    case 'size': return j.custSize || '—';
    case 'board': return j.paperType || '—';
    case 'gsm': return j.gsm || '—';
    case 'poQty': return j.totalBoxes && j.totalBoxes !== '0' ? j.totalBoxes : '—';
    case 'actual': return x.sheetSplit.base || <span className="text-slate-300">—</span>;
    case 'sheets':
      return (
        <>
          {j.totalSheets || '—'}
          {x.sheetSplit.wastage && x.sheetSplit.wastage !== '0' && (
            <span className="ml-1 text-[10px] font-normal text-amber-600" title="Wastage sheets">(+{x.sheetSplit.wastage})</span>
          )}
        </>
      );
    case 'dod':
      return j.promiseDate ? (
        <span className={x.urgent ? 'text-red-600 font-semibold' : ''}>
          {fmtDate(j.promiseDate)}
          {x.days !== null && <span className="text-[10px] ml-1 text-slate-500">({x.days}d)</span>}
        </span>
      ) : <span className="text-slate-400">—</span>;
    case 'poDate': return j.poDate ? fmtDate(j.poDate) : '—';
    case 'boardStatus': return <BoardBadge status={j.boardStatus} expectedDate={j.boardExpectedDate} />;
    default: return null;
  }
}

/* ── main component ────────────────────────────────────────── */

export function PlannerPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [sp] = useSearchParams();
  const canAct = can(user, 'VIEW_PLANNER');

  const [selMachine, setSelMachine] = useState(() => sp.get('machine') || '');
  const [date, setDate] = useState(() => toISO(new Date()));
  const [search, setSearch] = useState('');
  const [peek, setPeek] = useState<PeekTarget | null>(null);
  const [resolveDialog, setResolveDialog] = useState<PlanEntry | null>(null);
  const [closeReason, setCloseReason] = useState('');
  const [dragJob, setDragJob] = useState<string | null>(null);
  const [dragOverPlan, setDragOverPlan] = useState(false);
  // Plan-view re-sequencing: the row being dragged and the row it is over.
  // Plan-view row being dragged by its handle (pointer events: mouse and
  // touch alike), the row it is over, and a machine tab it is over.
  const [ptr, setPtr] = useState<{ src: number; overIdx: number | null; overMachine: string | null } | null>(null);
  const ptrRef = useRef(ptr);
  ptrRef.current = ptr;
  const [view, setView] = useState<'jobs' | 'plan'>('jobs');
  // Per-machine manual sequence the operator dragged into place — kept in
  // localStorage (per browser, per user) so it survives a reload without
  // needing a schema change.  Sort falls back to promise date for anything
  // not in this map, so a fresh job slots in naturally without wiping the
  // sequence.
  const [manualJobOrder, setManualJobOrder] = useState<Record<string, string[]>>(() => {
    try { return JSON.parse(localStorage.getItem('planner-jobs-order') || '{}'); } catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem('planner-jobs-order', JSON.stringify(manualJobOrder)); } catch { /* quota — ignore */ }
  }, [manualJobOrder]);
  // Which row the drag cursor is hovering over inside the Jobs table, and
  // which row the drag started from, so we can draw an insertion line on the
  // side of the hovered row where the dragged one will land.
  const [dragOverJobIdx, setDragOverJobIdx] = useState<number | null>(null);
  const [dragJobIdx, setDragJobIdx] = useState<number | null>(null);
  // The Available Jobs columns, as this browser has laid them out.
  const jobCols = useJobColumns();

  // Auto-scroll the Jobs table while a row drag is in progress — HTML5 DnD
  // doesn't do this on its own for overflow containers, so a drag toward the
  // Board / GSM / PO Qty columns off-screen would otherwise stall against
  // the visible edge.
  const jobsScrollRef = useRef<HTMLDivElement>(null);
  const dragScroll = useRef<{ raf: number | null; x: number; y: number }>({ raf: null, x: 0, y: 0 });
  const stopDragAutoScroll = useCallback(() => {
    if (dragScroll.current.raf != null) {
      cancelAnimationFrame(dragScroll.current.raf);
      dragScroll.current.raf = null;
    }
  }, []);
  const startDragAutoScroll = useCallback(() => {
    if (dragScroll.current.raf != null) return;
    const EDGE = 70;   // px from an edge where scrolling kicks in
    const MAX  = 22;   // max px per frame at the very edge
    const tick = () => {
      const el = jobsScrollRef.current;
      if (!el) { dragScroll.current.raf = null; return; }
      const rect = el.getBoundingClientRect();
      const { x, y } = dragScroll.current;
      let dx = 0, dy = 0;
      if (x > 0) {
        if (x < rect.left + EDGE) dx = -MAX * ((rect.left + EDGE - x) / EDGE);
        else if (x > rect.right - EDGE) dx = MAX * ((x - (rect.right - EDGE)) / EDGE);
      }
      if (y > 0) {
        if (y < rect.top + EDGE) dy = -MAX * ((rect.top + EDGE - y) / EDGE);
        else if (y > rect.bottom - EDGE) dy = MAX * ((y - (rect.bottom - EDGE)) / EDGE);
      }
      if (dx !== 0) el.scrollLeft += dx;
      if (dy !== 0) el.scrollTop += dy;
      dragScroll.current.raf = requestAnimationFrame(tick);
    };
    dragScroll.current.raf = requestAnimationFrame(tick);
  }, []);
  // Belt-and-braces: a drop outside the container fires dragend on the source
  // row (which our row handler catches), but a browser-swallowed dragend from
  // dropping on some other window doesn't — a window-level listener makes
  // sure the RAF loop never runs forever.
  useEffect(() => {
    const stop = () => stopDragAutoScroll();
    window.addEventListener('dragend', stop);
    window.addEventListener('drop', stop);
    return () => {
      window.removeEventListener('dragend', stop);
      window.removeEventListener('drop', stop);
      stopDragAutoScroll();
    };
  }, [stopDragAutoScroll]);
  // Central modal state for filling / correcting actuals + wastage.  Lifting
  // it out of the row means one modal instance regardless of how many rows
  // the plan has, and Super Admin can reopen it on a row that's already
  // finished to correct a typo.
  const [actualsTarget, setActualsTarget] = useState<PlanEntry | null>(null);
  const [filterBoard, setFilterBoard] = useState<'all' | 'available' | 'not_available'>('all');
  const [filterCoating, setFilterCoating] = useState<'all' | 'online' | 'offline'>('all');

  useEffect(() => {
    const m = sp.get('machine');
    if (m) setSelMachine(m);
  }, [sp]);

  /* ── queries ───────────────────────────────────────────── */

  const { data: machineData } = useQuery({
    queryKey: ['planner-machines'],
    queryFn: () => api<Machine[]>('/api/planner/machines'),
    staleTime: 300_000,
  });

  const machines = machineData ?? [];

  useEffect(() => {
    if (!selMachine && machines.length && machines[0]) setSelMachine(machines[0].id);
  }, [machines, selMachine]);

  // 30-second staleTime plus refetchOnWindowFocus off: the planner is a
  // multi-tab tool ("switch back to the tab to show a colleague"), and
  // every focus was kicking off three refetches that redrew the whole
  // table.  Explicit refresh actions (mutations, filter changes) still
  // invalidate the cache and pull fresh data.
  const { data: jobData, isLoading: loadingJobs } = useQuery({
    queryKey: ['planner-jobs', selMachine],
    queryFn: () => api<PlannerData>(`/api/planner?machine=${encodeURIComponent(selMachine)}`),
    enabled: !!selMachine,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const { data: planData, isLoading: loadingPlans } = useQuery({
    queryKey: ['planner-plans', date, selMachine],
    queryFn: () => api<PlanEntry[]>(`/api/planner/plans?date=${date}&machine=${encodeURIComponent(selMachine)}`),
    enabled: !!selMachine,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const { data: pendingActuals } = useQuery({
    queryKey: ['planner-pending', selMachine],
    queryFn: () => api<PendingActual[]>(`/api/planner/pending-actuals?machine=${encodeURIComponent(selMachine)}`),
    enabled: !!selMachine,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const allJobs = jobData?.jobs ?? [];
  const plans = planData ?? [];
  const pending = pendingActuals ?? [];
  const isToday = date === toISO(new Date());
  const isPast = date < toISO(new Date());

  const planJobKeys = useMemo(() => new Set(plans.map(p => `${p.jobId}|${p.passNo}`)), [plans]);

  // Debounced so the whole 27-row filter+sort doesn't run on every keystroke —
  // the user sees the search box respond immediately (React tracks `search`)
  // and the filtered list updates 200ms after they pause typing.
  const debouncedSearch = useDebounced(search, 200);

  // Hoisted out of the row map: `machines.filter(...)` ran once per row —
  // O(rows × machines) — on every render.  Now it runs once per render.
  const otherMachines = useMemo(
    () => machines.filter(m => m.id !== selMachine),
    [machines, selMachine],
  );

  // Every row that can still be planned here, in the operator's order: rows
  // dragged into place first, the rest by promise date, so a new job still
  // lands in the right place.  Each row keeps its own place — a job's front
  // and back rows are moved separately.
  const ordered = useMemo(() => {
    const list = allJobs
      .filter(j => j.jobStatus !== 'Printed')
      .filter(j => !planJobKeys.has(`${j.jobId}|${j.passNo}`));
    const manual = manualJobOrder[selMachine] ?? [];
    const orderIdx = new Map<string, number>();
    manual.forEach((k, i) => { if (!orderIdx.has(k)) orderIdx.set(k, i); });
    // An order saved before rows had their own keys holds job numbers only.
    const pos = (j: PlannerJob) => orderIdx.get(rowKey(j)) ?? orderIdx.get(j.jobId) ?? Infinity;
    return list.sort((a, b) => {
      const ma = pos(a);
      const mb = pos(b);
      if (ma !== mb) return ma - mb;
      const da = a.promiseDate ? new Date(a.promiseDate).getTime() : Infinity;
      const db = b.promiseDate ? new Date(b.promiseDate).getTime() : Infinity;
      return da !== db ? da - db : a.passNo - b.passNo;
    });
  }, [allJobs, planJobKeys, manualJobOrder, selMachine]);

  // What the filters and the search leave of it.
  const available = useMemo(() => {
    let list = ordered;
    if (filterBoard === 'available') list = list.filter(j => j.boardStatus === 'Available');
    if (filterBoard === 'not_available') list = list.filter(j => j.boardStatus !== 'Available');
    if (filterCoating === 'online') list = list.filter(j => j.coating?.toLowerCase().includes('online'));
    if (filterCoating === 'offline') list = list.filter(j => j.coating?.toLowerCase().includes('offline'));
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      list = list.filter(j =>
        j.jobId.toLowerCase().includes(q) ||
        j.client.toLowerCase().includes(q) ||
        j.productName.toLowerCase().includes(q) ||
        j.jobName.toLowerCase().includes(q)
      );
    }
    return list;
  }, [ordered, debouncedSearch, filterBoard, filterCoating]);

  /* ── mutations ─────────────────────────────────────────── */

  const invalidate = useCallback(() => {
    qc.refetchQueries({ queryKey: ['planner-jobs'] });
    qc.refetchQueries({ queryKey: ['planner-plans'] });
    qc.refetchQueries({ queryKey: ['planner-pending'] });
  }, [qc]);

  const addPlan = useMutation({
    mutationFn: (body: { date: string; machineId: string; jobId: string; passNo: number; plannedQty: number }) =>
      api('/api/planner/plans', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: invalidate,
    onError: (e: Error) => alert(`Could not add to the plan: ${e.message}`),
  });

  const updatePlan = useMutation({
    mutationFn: ({ id, ...body }: { id: number; plannedQty?: number; actualQty?: number; wastageQty?: number }) =>
      api(`/api/planner/plans/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    onSuccess: invalidate,
  });

  const reorderPlan = useMutation({
    mutationFn: (planIds: number[]) =>
      api('/api/planner/plans/reorder', { method: 'PUT', body: JSON.stringify({ planIds }) }),
    // Settled, not success: the rows move before the save lands, so a failed
    // save has to refetch to put them back where the server has them.
    onSettled: invalidate,
    onError: (e: Error) => alert(`Could not save the new order: ${e.message}`),
  });

  const movePlanMachine = useMutation({
    mutationFn: ({ id, machineId }: { id: number; machineId: string }) =>
      api(`/api/planner/plans/${id}/move`, { method: 'PUT', body: JSON.stringify({ machineId }) }),
    onSettled: invalidate,
    onError: (e: Error) => alert(
      e.message === 'already_on_that_machine' ? 'That job is already in that machine\'s plan for this day.'
      : e.message === 'already_printed' ? 'Printing was already recorded against this row, so it stays on its machine.'
      : `Could not move the job: ${e.message}`),
  });

  const resolvePlan = useMutation({
    mutationFn: ({ id, action, reason }: { id: number; action: 'close' | 'keep_open'; reason?: string }) =>
      api(`/api/planner/plans/${id}/resolve`, { method: 'PUT', body: JSON.stringify({ action, reason }) }),
    onSuccess: () => { invalidate(); setResolveDialog(null); setCloseReason(''); },
  });

  const removePlan = useMutation({
    mutationFn: (id: number) =>
      api(`/api/planner/plans/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
    onError: (e: Error) => {
      alert(e.message === 'not_removable'
        ? 'This row has actuals recorded — only Super Admin can undo it.'
        : `Undo failed: ${e.message}`);
    },
  });

  // Move a job from the current machine to another one. The API writes to
  // tblJobSpecification.PlannerMachine, which the queue query reads FIRST
  // before falling back to Machine / product master. So the job vanishes
  // from this tab and shows up on the target machine's tab.
  const reassignJob = useMutation({
    mutationFn: ({ jobId, machine }: { jobId: string; machine: string }) =>
      api('/api/planner/reassign', { method: 'PUT', body: JSON.stringify({ jobId, machine }) }),
    onSuccess: invalidate,
  });

  /* ── drag handlers ─────────────────────────────────────── */

  const handleDragStartAvail = (j: PlannerJob, idx: number, e: React.DragEvent) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ jobId: j.jobId, passNo: j.passNo, totalSheets: j.totalSheets }));
    // Second dataTransfer key marks THIS drag as a Jobs-view reorder so the
    // row drop target below can distinguish it from an external drag.  It
    // carries the row's own key: a job with a back pass has two rows.
    e.dataTransfer.setData(JOB_ROW_DRAG, rowKey(j));
    e.dataTransfer.effectAllowed = 'copyMove';
    setDragJob(rowKey(j));
    setDragJobIdx(idx);
    // Seed the auto-scroll tracker with the current pointer so the RAF loop
    // has a valid position before the first onDragOver fires.
    dragScroll.current.x = e.clientX;
    dragScroll.current.y = e.clientY;
    startDragAutoScroll();
  };

  const handleJobRowDrop = (target: PlannerJob, e: React.DragEvent) => {
    e.preventDefault();
    setDragOverJobIdx(null);
    setDragJob(null);
    setDragJobIdx(null);
    stopDragAutoScroll();
    const src = e.dataTransfer.getData(JOB_ROW_DRAG);
    if (!src) return; // not our drag (e.g., something dragged in from outside)
    const dst = rowKey(target);
    if (src === dst) return;
    // Keyed by row, not job: by job number, a job's front and back rows
    // shared one place and could not be moved apart.  Moved within the whole
    // list, so rows a filter or the search hides keep their places.
    const keys = ordered.map(rowKey);
    const from = keys.indexOf(src);
    const to = keys.indexOf(dst);
    if (from < 0 || to < 0) return;
    keys.splice(from, 1);
    // Dropping onto row N places the source at row N: below the target when
    // it came from above (the removal shifted the target up), above it when
    // it came from below.
    keys.splice(to, 0, src);
    setManualJobOrder(prev => ({ ...prev, [selMachine]: keys }));
  };

  const clearJobOrder = () => {
    setManualJobOrder(prev => {
      const next = { ...prev };
      delete next[selMachine];
      return next;
    });
  };
  const hasManualOrder = (manualJobOrder[selMachine]?.length ?? 0) > 0;

  const handleDropOnPlan = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverPlan(false);
    setDragJob(null);
    try {
      const d = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (d.jobId && selMachine) {
        addPlan.mutate({
          date, machineId: selMachine, jobId: d.jobId, passNo: d.passNo ?? 1,
          plannedQty: parseInt(d.totalSheets) || 0,
        });
      }
    } catch { /* ignore */ }
  };

  /** Put the plan in a new order: optimistic, then saved. */
  const applyOrder = (next: PlanEntry[]) => {
    qc.setQueryData(['planner-plans', date, selMachine], next);
    reorderPlan.mutate(next.map(p => p.planId));
  };
  const moveRow = (srcIdx: number, targetIdx: number) => {
    if (srcIdx === targetIdx || targetIdx < 0 || targetIdx >= plans.length) return;
    const next = [...plans];
    const [moved] = next.splice(srcIdx, 1);
    if (!moved) return;
    next.splice(targetIdx, 0, moved);
    applyOrder(next);
  };

  // Drag by the row's handle.  Pointer events, not HTML drag-and-drop: they
  // work the same with a mouse and a finger, and a drop is judged from what
  // is under the pointer when it is let go — a row (re-sequence) or another
  // machine's tab (move the job to that machine).
  const startHandleDrag = (idx: number, e: React.PointerEvent) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    e.preventDefault();
    setPtr({ src: idx, overIdx: null, overMachine: null });
    const under = (x: number, y: number) => {
      const el = document.elementFromPoint(x, y) as HTMLElement | null;
      const row = el?.closest('[data-plan-idx]') as HTMLElement | null;
      const tab = el?.closest('[data-machine-drop]') as HTMLElement | null;
      return {
        overIdx: row ? Number(row.dataset.planIdx) : null,
        overMachine: tab ? tab.dataset.machineDrop ?? null : null,
      };
    };
    const onMove = (ev: PointerEvent) => {
      ev.preventDefault();
      const u = under(ev.clientX, ev.clientY);
      setPtr(p => (p && (p.overIdx !== u.overIdx || p.overMachine !== u.overMachine) ? { ...p, ...u } : p));
    };
    const finish = (ev: PointerEvent, cancelled: boolean) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      const cur = ptrRef.current;
      setPtr(null);
      if (cancelled || !cur) return;
      const u = under(ev.clientX, ev.clientY);
      const entry = plans[cur.src];
      if (u.overMachine && u.overMachine !== selMachine && entry) {
        movePlanMachine.mutate({ id: entry.planId, machineId: u.overMachine });
      } else if (u.overIdx != null) {
        moveRow(cur.src, u.overIdx);
      }
    };
    const onUp = (ev: PointerEvent) => finish(ev, false);
    const onCancel = (ev: PointerEvent) => finish(ev, true);
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
  };

  /* ── actual qty save + resolve trigger ─────────────────── */

  const handleActualSave = (entry: PlanEntry, actual: number, wastage: number, plannedQty?: number) => {
    updatePlan.mutate({ id: entry.planId, actualQty: actual, wastageQty: wastage, ...(plannedQty !== undefined ? { plannedQty } : {}) }, {
      onSuccess: () => {
        if (actual + wastage < entry.plannedQty) {
          setResolveDialog({ ...entry, actualQty: actual, wastageQty: wastage });
        } else {
          resolvePlan.mutate({ id: entry.planId, action: 'close' });
        }
      },
    });
  };

  /* ── date nav ──────────────────────────────────────────── */

  const shiftDate = (n: number) => {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    setDate(toISO(d));
  };

  const loading = loadingJobs || loadingPlans;
  const curMachine = machines.find(m => m.id === selMachine);

  return (
    <div className="p-4 sm:p-6 mx-auto">
      <style>{`
        @media print {
          body, .layout-root, .layout-container, .layout-main { display: block !important; margin: 0 !important; padding: 0 !important; overflow: visible !important; }
          .layout-root > header, aside { display: none !important; }
          .print\\:hidden { display: none !important; }
          .print\\:block { display: block !important; }
        }
      `}</style>

      {/* header */}
      <div className="flex flex-wrap items-center gap-3 mb-4 print:hidden">
        <h1 className="text-xl font-bold text-slate-800">Printing Planning</h1>
        <span className="text-sm text-slate-500">{curMachine?.displayName ?? 'Select machine'}</span>
      </div>

      {/* machine tabs + view toggle */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-4 border-b border-slate-200 print:hidden">
        <div className="flex gap-1 flex-1">
          {machines.map(m => (
            <button key={m.id} onClick={() => setSelMachine(m.id)}
              data-machine-drop={m.id}
              title={ptr && m.id !== selMachine ? `Drop to move the job to ${m.displayName}` : undefined}
              className={`px-3 py-1.5 rounded-t text-sm font-medium whitespace-nowrap transition-colors ${
                selMachine === m.id ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100'
              } ${ptr && m.id !== selMachine ? 'ring-1 ring-dashed ring-brand/40' : ''} ${
                ptr?.overMachine === m.id && m.id !== selMachine ? 'bg-brand-100 ring-2 ring-brand text-brand' : ''
              }`}>{m.displayName}</button>
          ))}
        </div>
        <div className="flex rounded-lg border border-slate-300 overflow-hidden flex-shrink-0">
          <button onClick={() => setView('jobs')}
            className={`px-3 py-1.5 text-xs font-medium ${view === 'jobs' ? 'bg-brand text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
            Jobs
          </button>
          <button onClick={() => setView('plan')}
            className={`px-3 py-1.5 text-xs font-medium border-l border-slate-300 ${view === 'plan' ? 'bg-brand text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
            Plan {plans.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-white/20 text-[10px]">{plans.length}</span>}
          </button>
        </div>
      </div>

      {/* pending actuals banner */}
      {pending.length > 0 && (
        <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-2.5 flex items-center gap-3 print:hidden">
          <span className="text-amber-600 text-lg">&#9888;</span>
          <div className="text-sm text-amber-800">
            <strong>Pending actuals:</strong>{' '}
            {pending.map(p => (
              <button key={p.date} onClick={() => { setDate(p.date); setView('plan'); }}
                className="underline hover:text-amber-900 mr-2">
                {fmtDate(p.date)} ({p.count})
              </button>
            ))}
          </div>
        </div>
      )}

      {/* date nav */}
      <div className="flex items-center gap-2 mb-4 print:hidden">
        <button onClick={() => shiftDate(-1)} className="px-2 py-1 rounded border border-slate-300 text-sm hover:bg-slate-50">&larr;</button>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          className="rounded border border-slate-300 px-3 py-1 text-sm focus:border-brand focus:ring-1 focus:ring-brand" />
        <button onClick={() => shiftDate(1)} className="px-2 py-1 rounded border border-slate-300 text-sm hover:bg-slate-50">&rarr;</button>
        {!isToday && (
          <button onClick={() => setDate(toISO(new Date()))} className="px-3 py-1 rounded text-sm text-brand hover:underline">Today</button>
        )}
        <span className="text-sm text-slate-500 ml-2">
          {isToday ? 'Today' : isPast ? 'Past' : 'Future'} &middot; {fmtDate(date)}
        </span>
        {view === 'jobs' && (
          <div className="ml-auto flex items-center gap-2">
            <select value={filterBoard} onChange={e => setFilterBoard(e.target.value as typeof filterBoard)}
              className="rounded border border-slate-300 px-2 py-1 text-xs focus:border-brand focus:ring-1 focus:ring-brand">
              <option value="all">Board: All</option>
              <option value="available">Board: Available</option>
              <option value="not_available">Board: Not Available</option>
            </select>
            <select value={filterCoating} onChange={e => setFilterCoating(e.target.value as typeof filterCoating)}
              className="rounded border border-slate-300 px-2 py-1 text-xs focus:border-brand focus:ring-1 focus:ring-brand">
              <option value="all">Coating: All</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
            </select>
            <input type="text" placeholder="Search jobs..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-48 rounded border border-slate-300 px-2 py-1 text-xs focus:border-brand focus:ring-1 focus:ring-brand" />
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 py-8 text-center">Loading...</div>
      ) : !selMachine ? (
        <div className="text-sm text-slate-400 py-8 text-center">Select a machine to begin planning</div>
      ) : view === 'jobs' ? (
        /* ── JOBS VIEW: full-width columnar table ──────────── */
        <div className="bg-white rounded-lg shadow print:hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
            <h2 className="font-semibold text-slate-700 text-sm">
              Available Jobs — {curMachine?.displayName}
              <span className="ml-2 font-normal text-slate-400">
                ({available.length} jobs
                {' · '}
                {available.reduce((s, j) => s + (parseInt(j.totalSheets) || 0), 0).toLocaleString()} sheets)
              </span>
              {hasManualOrder && (
                <span className="ml-2 px-1.5 py-0.5 rounded bg-brand/10 text-brand text-[10px] font-medium align-middle">Custom order</span>
              )}
            </h2>
            <div className="flex items-center gap-3">
              {jobCols.customized && (
                <button
                  type="button"
                  onClick={jobCols.reset}
                  title="Put the columns back in their usual order and widths"
                  className="text-xs text-slate-500 hover:text-brand underline decoration-dotted whitespace-nowrap"
                >
                  Reset columns
                </button>
              )}
              {hasManualOrder && (
                <button
                  type="button"
                  onClick={clearJobOrder}
                  title="Drop the manual sequence and go back to the promise-date order"
                  className="text-xs text-slate-500 hover:text-brand underline decoration-dotted whitespace-nowrap"
                >
                  Reset order
                </button>
              )}
            </div>
          </div>
          <div
            ref={jobsScrollRef}
            onDragOver={e => { dragScroll.current.x = e.clientX; dragScroll.current.y = e.clientY; }}
            className="overflow-x-auto max-h-[70vh] overflow-y-auto"
          >
            {/* Excel-style grid, same as the plan table: drag a header to move
                its column, drag a header's edge to size it.  Fixed layout, so
                the widths hold and longer text is cut short (its tooltip has
                it all). */}
            <table
              className="text-xs [&_td]:border [&_td]:border-slate-300 [&_th]:border [&_th]:border-slate-300"
              style={{ tableLayout: 'fixed', width: ACTION_COL_WIDTH + jobCols.cols.reduce((s, c) => s + c.width, 0) }}
            >
              <colgroup>
                <col style={{ width: ACTION_COL_WIDTH }} />
                {jobCols.cols.map(c => <col key={c.id} style={{ width: c.width }} />)}
              </colgroup>
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                <tr>
                  <th className="px-2 py-2 text-left font-medium text-slate-600"></th>
                  {jobCols.cols.map(c => (
                    <ColHeader key={c.id} col={c} onMove={jobCols.move} onResize={jobCols.resize} onDefaultWidth={jobCols.defaultWidth} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {available.length === 0 ? (
                  <tr><td colSpan={jobCols.cols.length + 1} className="px-2 py-6 text-center text-slate-400">No available jobs for this machine</td></tr>
                ) : available.map((j, idx) => {
                  const days = dodDays(j.promiseDate);
                  const urgent = days !== null && days <= 5;
                  // A row dropped here lands below this one when it comes from
                  // above, and above it when it comes from below.
                  const dropLine = dragOverJobIdx !== idx || dragJobIdx === null || dragJobIdx === idx
                    ? null : dragJobIdx < idx ? 'below' : 'above';
                  const ctx: RowCtx = {
                    days,
                    urgent,
                    hist: jobHistoryChip(j.orderType),
                    sheetSplit: splitSheets(j.clientQty, j.ups, j.totalSheets),
                    // Machine chain for the multi-pass badge — includes back as
                    // its own step, so a job with front, front2 and back reads
                    // as "3P HB-6COL→HB-4COL→CD-102".
                    passChain: [j.pass1Machine, j.pass2Machine, j.pass3Machine].filter(Boolean).join('→'),
                  };
                  const key = rowKey(j);
                  return (
                    <tr key={key}
                      draggable={canAct}
                      onDragStart={e => handleDragStartAvail(j, idx, e)}
                      onDragEnd={() => { setDragJob(null); setDragJobIdx(null); setDragOverJobIdx(null); stopDragAutoScroll(); }}
                      onDragOver={canAct ? (e => {
                        // Only our own row drags — not a column header, nor
                        // anything dragged in from elsewhere.
                        if (!e.dataTransfer.types.includes(JOB_ROW_DRAG)) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        if (dragOverJobIdx !== idx) setDragOverJobIdx(idx);
                      }) : undefined}
                      onDragLeave={() => { if (dragOverJobIdx === idx) setDragOverJobIdx(null); }}
                      onDrop={canAct ? (e => handleJobRowDrop(j, e)) : undefined}
                      style={dropLine ? { boxShadow: `inset 0 ${dropLine === 'above' ? 2 : -2}px 0 0 #014868` } : undefined}
                      className={`border-t border-slate-100 ${
                        canAct ? 'cursor-grab active:cursor-grabbing' : ''
                      } ${urgent ? 'bg-red-50 hover:bg-red-100' : 'hover:bg-slate-50'} ${
                        dragJob === key ? 'opacity-50' : ''
                      }`}>
                      <td className="px-2 py-1.5">
                        {canAct && !isPast && (
                          <div className="flex items-center gap-1">
                            <button title="Add to plan" onClick={() => addPlan.mutate({ date, machineId: selMachine, jobId: j.jobId, passNo: j.passNo, plannedQty: parseInt(j.totalSheets) || 0 })}
                              className="w-5 h-5 rounded bg-brand text-white text-xs font-bold hover:bg-brand/80 leading-none">+</button>
                            {/* Move this job to a different machine. Value is
                                reset after selection so the same target can be
                                picked again on the next row. */}
                            <select
                              title="Move to another machine"
                              value=""
                              disabled={reassignJob.isPending}
                              onChange={e => {
                                const target = e.target.value;
                                if (target && target !== selMachine) {
                                  reassignJob.mutate({ jobId: j.jobId, machine: target });
                                }
                                e.target.value = '';
                              }}
                              onClick={e => e.stopPropagation()}
                              className="w-5 h-5 rounded border border-slate-300 text-slate-600 bg-white text-[10px] leading-none cursor-pointer hover:border-brand hover:text-brand appearance-none text-center"
                            >
                              <option value="">⇄</option>
                              {otherMachines.map(m => (
                                <option key={m.id} value={m.id}>{m.displayName}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </td>
                      {jobCols.cols.map(c => {
                        const content = jobCell(c.id, j, ctx, setPeek);
                        const tip = typeof content === 'string' ? content
                          : c.id === 'sheets' && ctx.sheetSplit.wastage ? `Wastage: ${ctx.sheetSplit.wastage}` : undefined;
                        return (
                          <td key={c.id} title={tip}
                            className={`px-2 py-1.5 align-top overflow-hidden ${
                              c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : ''
                            } ${c.wrap ? 'whitespace-normal break-words' : 'whitespace-nowrap text-ellipsis'} ${c.cls ?? ''}`}>
                            {content}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-1.5 text-xs text-slate-400 border-t border-slate-100">
            {available.length} job{available.length !== 1 ? 's' : ''} available
            {canAct && ' — drag rows to build a sequence'}
            {canAct && !isPast && ', click + or drag to plan view to add'}
            {' · drag a column header to move it, its edge to size it'}
          </div>
        </div>
      ) : (
        /* ── PLAN VIEW: machine-wise plan ─────────────────── */
        <div
          onDragOver={e => {
            e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setDragOverPlan(true);
          }}
          onDragLeave={() => setDragOverPlan(false)}
          onDrop={handleDropOnPlan}
          className={`bg-white rounded-lg shadow transition-all min-h-[300px] ${
            dragOverPlan ? 'ring-2 ring-brand ring-offset-2 bg-blue-50' : ''
          }`}
        >
          <div className="px-4 py-3 border-b border-slate-200 flex items-start justify-between print:hidden">
            <div>
              <h2 className="font-semibold text-slate-700 text-sm">
                {curMachine?.displayName} — {isToday ? "Today's Plan" : `Plan for ${fmtDate(date)}`}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {plans.length} job{plans.length !== 1 ? 's' : ''} planned
                {plans.length > 0 && (
                  <>
                    {' · '}
                    {plans.reduce((s, p) => s + (p.plannedQty || 0), 0).toLocaleString()} sheets
                  </>
                )}
                {canAct && !isPast && ' — drag jobs here or switch to Jobs view to add'}
              </p>
            </div>
            {plans.length > 0 && (
              <div className="flex gap-1 print:hidden">
                <button onClick={() => downloadPlanCSV(plans, curMachine?.displayName ?? selMachine, date)}
                  className="px-3 py-1.5 rounded border border-slate-300 text-xs text-slate-600 hover:bg-slate-50 flex items-center gap-1" title="Download Excel">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  Excel
                </button>
                <button onClick={() => window.print()}
                  className="px-3 py-1.5 rounded border border-slate-300 text-xs text-slate-600 hover:bg-slate-50 flex items-center gap-1" title="Print this plan">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V2h12v7"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                  Print
                </button>
              </div>
            )}
          </div>
          {/* print header */}
          <div className="hidden print:block px-4 py-3 border-b border-slate-200">
            <h1 className="text-base font-bold">VASUDEV PRINT PACK PVT. LTD.</h1>
            <p className="text-xs text-slate-600">
              Machine: <strong>{curMachine?.displayName}</strong> &middot; Date: <strong>{fmtDate(date)}</strong> &middot; Jobs: <strong>{plans.length}</strong>
            </p>
          </div>

          {plans.length === 0 ? (
            <div className={`px-4 py-12 text-center ${dragOverPlan ? 'text-brand' : 'text-slate-400'}`}>
              <div className="text-3xl mb-2">{dragOverPlan ? '+' : '\u{1F4CB}'}</div>
              <p className="text-sm">{!isPast ? 'Switch to Jobs view to add jobs, or drag them here' : 'No plan for this date'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              {/* Excel-style grid: every cell boxed, so each job reads as its own row. */}
              <table className="w-full text-xs whitespace-nowrap [&_td]:border [&_td]:border-slate-300 [&_th]:border [&_th]:border-slate-300">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                  <tr>
                    <th className="px-2 py-2 text-center font-medium text-slate-600 w-8">#</th>
                    <th className="px-2 py-2 text-left font-medium text-slate-600">Job</th>
                    <th className="px-2 py-2 text-left font-medium text-slate-600">Client</th>
                    <th className="px-2 py-2 text-left font-medium text-slate-600">Product</th>
                    <th className="px-2 py-2 text-left font-medium text-slate-600">Design</th>
                    <th className="px-2 py-2 text-center font-medium text-slate-600">UPS</th>
                    <th className="px-2 py-2 text-left font-medium text-slate-600">Colors</th>
                    <th className="px-2 py-2 text-left font-medium text-slate-600">Top Finish</th>
                    <th className="px-2 py-2 text-left font-medium text-slate-600">Size</th>
                    <th className="px-2 py-2 text-left font-medium text-slate-600">Board</th>
                    <th className="px-2 py-2 text-center font-medium text-slate-600">GSM</th>
                    <th className="px-2 py-2 text-left font-medium text-slate-600">DOD</th>
                    <th className="px-2 py-2 text-right font-medium text-slate-600">Planned</th>
                    <th className="px-2 py-2 text-right font-medium text-slate-600">Actual</th>
                    <th className="px-2 py-2 text-right font-medium text-slate-600">Wastage</th>
                    <th className="px-2 py-2 text-center font-medium text-slate-600">Status</th>
                    <th className="px-2 py-2 text-center font-medium text-slate-600 w-8 print:hidden"></th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((entry, idx) => (
                    <PlanTableRow
                      key={entry.planId}
                      entry={entry}
                      idx={idx}
                      canAct={canAct}
                      isToday={isToday}
                      isPast={isPast}
                      isSuperAdmin={user?.rollId === ROLE.SuperAdmin}
                      onRemove={() => removePlan.mutate(entry.planId)}
                      onOpenActuals={() => setActualsTarget(entry)}
                      onPeek={(id) => setPeek({ kind: 'job', id })}
                      dragging={ptr?.src === idx}
                      // The dragged row lands below a row it moves down onto
                      // and above one it moves up onto — draw the line there.
                      dropLine={!ptr || ptr.src === idx || ptr.overIdx !== idx || ptr.overMachine
                        ? null : ptr.src < idx ? 'below' : 'above'}
                      movable={canAct && !isPast}
                      onHandleDown={e => startHandleDrag(idx, e)}
                      onUp={idx > 0 ? () => moveRow(idx, idx - 1) : undefined}
                      onDown={idx < plans.length - 1 ? () => moveRow(idx, idx + 1) : undefined}
                      otherMachines={machines.filter(m => m.id !== selMachine)}
                      onMoveMachine={(mid) => movePlanMachine.mutate({ id: entry.planId, machineId: mid })}
                      onPlannedSave={(qty) => updatePlan.mutate({ id: entry.planId, plannedQty: qty })}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* resolve dialog */}
      {resolveDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-slate-800 mb-2">Production Shortfall</h3>
            <p className="text-sm text-slate-600 mb-4">
              Job <strong className="font-mono">{resolveDialog.jobId.split('/').pop()}</strong> printed{' '}
              <strong>{resolveDialog.actualQty}</strong> of <strong>{resolveDialog.plannedQty}</strong> planned sheets.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">Reason for closing (required to close)</label>
              <textarea value={closeReason} onChange={e => setCloseReason(e.target.value)}
                placeholder="e.g. Client accepted short delivery, plate issue, etc."
                rows={2}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:ring-1 focus:ring-brand" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => resolvePlan.mutate({ id: resolveDialog.planId, action: 'close', reason: closeReason })}
                disabled={resolvePlan.isPending || !closeReason.trim()}
                className="flex-1 rounded-lg bg-green-600 px-4 py-2.5 text-sm text-white font-medium hover:bg-green-700 disabled:opacity-50">
                Close Job
              </button>
              <button onClick={() => resolvePlan.mutate({ id: resolveDialog.planId, action: 'keep_open' })}
                disabled={resolvePlan.isPending}
                className="flex-1 rounded-lg bg-amber-500 px-4 py-2.5 text-sm text-white font-medium hover:bg-amber-600 disabled:opacity-50">
                Keep Open
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              <strong>Close</strong> = printing done, requires a reason. <strong>Keep Open</strong> = remaining {resolveDialog.plannedQty - (resolveDialog.actualQty ?? 0) - (resolveDialog.wastageQty ?? 0)} sheets auto-added to tomorrow's plan.
            </p>
          </div>
        </div>
      )}

      {peek && <RecordPeek target={peek} onClose={() => setPeek(null)} />}

      {actualsTarget && (
        <ActualsModal
          entry={actualsTarget}
          onClose={() => setActualsTarget(null)}
          onSave={(a, w, planned) => {
            handleActualSave(planned !== actualsTarget.plannedQty ? { ...actualsTarget, plannedQty: planned } : actualsTarget, a, w,
              planned !== actualsTarget.plannedQty ? planned : undefined);
            setActualsTarget(null);
          }}
        />
      )}
    </div>
  );
}

/* ── PlanTableRow ──────────────────────────────────────────── */

function PlanTableRow({ entry, idx, canAct, isToday, isPast, isSuperAdmin, dragging, dropLine, onRemove, onOpenActuals, onPeek, movable, onHandleDown, onUp, onDown, otherMachines, onMoveMachine, onPlannedSave }: {
  entry: PlanEntry; idx: number; canAct: boolean; isToday: boolean; isPast: boolean;
  /** Super Admin can undo any plan row — even a partial/done one on a past day. */
  isSuperAdmin: boolean;
  /** This is the row being dragged to a new place in the sequence. */
  dragging: boolean;
  /** Where the dragged row would land if dropped on this one. */
  dropLine: 'above' | 'below' | null;
  onRemove: () => void;
  /** Open the Actuals + Wastage modal for this row. */
  onOpenActuals: () => void;
  onPeek: (id: string) => void;
  /** Can be re-sequenced (today or a later day, planner access). */
  movable: boolean;
  onHandleDown: (e: React.PointerEvent) => void;
  onUp?: () => void;
  onDown?: () => void;
  otherMachines: Machine[];
  onMoveMachine: (machineId: string) => void;
  onPlannedSave: (qty: number) => void;
}) {
  const [editPlanned, setEditPlanned] = useState('');
  const [editingPlanned, setEditingPlanned] = useState(false);

  const days = dodDays(entry.promiseDate);
  const urgent = days !== null && days <= 5;
  const done = entry.status === 'completed';
  const partial = entry.status === 'partial';
  const needsActual = entry.status === 'planned' && isPast;
  const passChain = [entry.pass1Machine, entry.pass2Machine, entry.pass3Machine].filter(Boolean).join('→');

  // Anyone with planner access can fill actuals for a still-planned row on
  // today or a past date.  Super Admin can also correct an already-filled
  // row's actuals, so a mistyped number does not need a delete-and-redo.
  const canFill  = canAct && (isPast || isToday) && entry.status === 'planned';
  const canEdit  = isSuperAdmin && entry.actualQty !== null;
  // The job card's actual sheets and wastage, shown until real ones are filled.
  const split = cardSplit(entry);

  const startEditPlanned = () => {
    setEditPlanned(entry.plannedQty.toString());
    setEditingPlanned(true);
  };

  const savePlanned = () => {
    const q = parseInt(editPlanned) || 0;
    setEditingPlanned(false);
    if (q !== entry.plannedQty) onPlannedSave(q);
  };

  const rowBg = done ? 'bg-green-50' : partial ? 'bg-amber-50' : needsActual ? 'bg-yellow-50' : urgent ? 'bg-red-50' : '';

  return (
    <tr
      data-plan-idx={idx}
      // With a mouse the whole row drags, as in the Jobs view — except from
      // its buttons and fields.  A finger uses the ⠿ handle, so the page
      // still scrolls when the row is swiped.
      onPointerDown={movable ? (e) => {
        if (e.pointerType !== 'mouse') return;
        if ((e.target as HTMLElement).closest('button, a, input, select, textarea, [data-no-drag]')) return;
        onHandleDown(e);
      } : undefined}
      style={dropLine ? { boxShadow: `inset 0 ${dropLine === 'above' ? 3 : -3}px 0 0 #014868` } : undefined}
      className={`border-t border-slate-100 ${rowBg} ${movable ? 'cursor-grab active:cursor-grabbing select-none' : ''} ${dragging ? 'opacity-50' : ''} hover:bg-slate-50/50`}
    >
      <td className="px-1 py-1.5 text-center">
        <div className="inline-flex items-center gap-0.5">
          {movable && (
            <span onPointerDown={(e) => { e.stopPropagation(); onHandleDown(e); }} style={{ touchAction: 'none' }}
                  className="cursor-grab active:cursor-grabbing select-none px-1 text-base leading-none text-slate-400 hover:text-brand print:hidden"
                  title="Drag to change the order — or onto another machine's tab to move it there">⠿</span>
          )}
          <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 inline-flex items-center justify-center font-bold text-[10px]">{idx + 1}</span>
        </div>
      </td>
      <td className="px-2 py-1.5 font-mono max-w-[120px] align-top">
        <div className="flex flex-wrap items-center gap-1">
          <button onClick={() => onPeek(entry.jobId)} className="text-brand hover:underline text-[11px]">
            {entry.jobId.split('/').pop()}
          </button>
          {entry.isBack
            ? <span className="px-1 py-0.5 rounded bg-sky-100 text-sky-700 text-[10px]" title="Back-side pass">Back</span>
            : entry.passNo === 2
              ? <span className="px-1 py-0.5 rounded bg-purple-100 text-purple-700 text-[10px]">2nd</span>
              : null}
          {entry.totalPasses >= 2 && (
            <span
              title={`${entry.totalPasses}-pass job — ${passChain || 'machine chain unknown'}`}
              className={`px-1 py-0.5 rounded text-[10px] ${entry.totalPasses === 3 ? 'bg-rose-100 text-rose-700' : 'bg-orange-100 text-orange-700'}`}
            >
              {entry.totalPasses}P{passChain ? ` ${passChain}` : ''}
            </span>
          )}
          {entry.isReissued && (
            <span className="px-1 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-semibold" title={`Reissued: ${entry.reissueReason || ''}`}>
              REISSUE{entry.reissueReason ? ` · ${entry.reissueReason}` : ''}
            </span>
          )}
        </div>
      </td>
      <td className="px-2 py-1.5 truncate max-w-[130px]">{entry.client}</td>
      <td className="px-2 py-1.5 max-w-[220px] whitespace-normal break-words align-top">{entry.productName || entry.jobName}</td>
      <td className="px-2 py-1.5 text-slate-600">{entry.designNo || '—'}</td>
      <td className="px-2 py-1.5 text-center">{entry.ups || '—'}</td>
      <td className="px-2 py-1.5 max-w-[180px] align-top">
        {(() => {
          const c = passColors(entry.colorsFront1, entry.colorsFront2, entry.colorsBack, entry.passNo, entry.totalPasses, entry.isBack ?? false);
          return c
            ? <span className="text-[10px] text-slate-600 break-words whitespace-normal">{c}</span>
            : '—';
        })()}
      </td>
      <td className="px-2 py-1.5 text-slate-600 max-w-[160px] whitespace-normal break-words align-top">{entry.topFinish || '—'}</td>
      <td className="px-2 py-1.5 text-slate-600">{entry.custSize || '—'}</td>
      <td className="px-2 py-1.5 text-slate-600">{entry.paperType || '—'}</td>
      <td className="px-2 py-1.5 text-center">{entry.gsm || '—'}</td>
      <td className="px-2 py-1.5">
        {entry.promiseDate ? (
          <span className={urgent ? 'text-red-600 font-semibold' : ''}>
            {fmtDate(entry.promiseDate)}
            {days !== null && <span className="text-[10px] ml-1 text-slate-500">({days}d)</span>}
          </span>
        ) : '—'}
      </td>
      <td className="px-2 py-1.5 text-right">
        {editingPlanned ? (
          <input type="number" value={editPlanned} onChange={e => setEditPlanned(e.target.value)}
            onBlur={savePlanned} onKeyDown={e => e.key === 'Enter' && savePlanned()}
            autoFocus className="w-16 rounded border border-slate-300 px-1.5 py-0.5 text-xs text-right" />
        ) : (
          <button onClick={canAct && entry.status === 'planned' ? startEditPlanned : undefined}
            className={`font-semibold ${canAct && entry.status === 'planned' ? 'hover:underline cursor-pointer' : ''}`}>
            {entry.plannedQty}
          </button>
        )}
      </td>
      <td className="px-2 py-1.5 text-right">
        {entry.actualQty !== null ? (
          <div className="inline-flex items-center gap-1 justify-end">
            <span className="font-medium tabular-nums">{entry.actualQty.toLocaleString()}</span>
            {canEdit && (
              <button
                onClick={onOpenActuals}
                className="text-slate-400 hover:text-brand text-[11px]"
                title="Edit actuals (Super Admin)"
              >✎</button>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-end gap-1">
            {split && <span className="tabular-nums text-slate-500" title="Actual sheets on the job card">{split.base.toLocaleString()}<span className="ml-0.5 text-[9px] font-semibold text-slate-400">JC</span></span>}
            {canFill ? (
              <button
                onClick={onOpenActuals}
                className="px-2.5 py-1 rounded bg-blue-600 text-white text-[11px] font-semibold shadow-sm hover:bg-blue-700"
              >Fill</button>
            ) : !split && <span className="text-slate-400">—</span>}
          </div>
        )}
      </td>
      <td className="px-2 py-1.5 text-right">
        {entry.wastageQty !== null && entry.wastageQty !== undefined ? (
          <span className="font-medium tabular-nums">{entry.wastageQty.toLocaleString()}</span>
        ) : split ? (
          <span className="tabular-nums text-slate-500" title="Wastage sheets on the job card">{split.wastage.toLocaleString()}<span className="ml-0.5 text-[9px] font-semibold text-slate-400">JC</span></span>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-2 py-1.5 text-center">
        {done && <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-medium">Done</span>}
        {partial && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-medium">Partial</span>}
        {needsActual && <span className="px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700 text-[10px] font-medium animate-pulse">Pending</span>}
        {!done && !partial && !needsActual && <span className="text-slate-400">—</span>}
      </td>
      <td className="px-2 py-1.5 text-center print:hidden">
        {/* Regular planners can only remove a still-planned row on today's
            date.  Super Admin gets an Undo button on every row, which the
            API mirrors (any status, any day) — the confirm prompt is there
            because this deletes the row and any actuals it carried. */}
        {movable && (
          <div className="mb-0.5 flex items-center justify-center gap-0.5 print:hidden">
            <button type="button" onClick={onUp} disabled={!onUp} title="Move up"
                    className="rounded px-1 text-slate-500 hover:bg-slate-100 disabled:opacity-25">↑</button>
            <button type="button" onClick={onDown} disabled={!onDown} title="Move down"
                    className="rounded px-1 text-slate-500 hover:bg-slate-100 disabled:opacity-25">↓</button>
            {entry.status === 'planned' && entry.actualQty == null && otherMachines.length > 0 && (
              <select value="" title="Move to another machine"
                      onChange={(e) => { if (e.target.value) onMoveMachine(e.target.value); }}
                      className="w-7 rounded border border-slate-200 bg-white text-[10px] text-slate-500">
                <option value="">⇄</option>
                {otherMachines.map(m => <option key={m.id} value={m.id}>→ {m.displayName}</option>)}
              </select>
            )}
          </div>
        )}
        {isSuperAdmin ? (
          <button
            onClick={() => {
              const hasActual = (entry.actualQty ?? 0) > 0 || (entry.wastageQty ?? 0) > 0;
              if (hasActual && !confirm('This plan row has actuals recorded. Undo will delete the row and its actuals. Continue?')) return;
              onRemove();
            }}
            className="text-slate-400 hover:text-red-500 text-xs leading-none px-1 py-0.5 rounded hover:bg-red-50"
            title="Undo (Super Admin)"
          >Undo</button>
        ) : (canAct && !isPast && entry.status === 'planned') ? (
          <button onClick={onRemove} className="text-slate-400 hover:text-red-500 text-sm leading-none" title="Remove">&times;</button>
        ) : null}
      </td>
    </tr>
  );
}

/* ── ActualsModal ──────────────────────────────────────────── */

/**
 * A single dialog that owns every actuals / wastage edit — for a fresh
 * "Fill" and for a Super-Admin correction.  Runs the maths live so the
 * operator sees whether their numbers add up before saving:
 *
 *   Actual + Wastage = Total ⟶ compared to Planned ⟶ green / amber / red
 *
 * Enter saves from either input, Escape closes.
 */
function ActualsModal({ entry, onClose, onSave }: {
  entry: PlanEntry;
  onClose: () => void;
  onSave: (actual: number, wastage: number, planned: number) => void;
}) {
  // Not filled yet: start from the job card's actual sheets and wastage.
  const card = entry.actualQty == null ? cardSplit(entry) : null;
  const [actual, setActual] = useState(entry.actualQty?.toString() ?? (card ? String(card.base) : ''));
  const [wastage, setWastage] = useState(entry.wastageQty?.toString() ?? (card ? String(card.wastage) : ''));
  // A row not yet filled may have its planned sheets changed here too.
  const plannedEditable = entry.status === 'planned';
  const [plannedText, setPlannedText] = useState(entry.plannedQty.toString());

  const a = Number(actual) || 0;
  const w = Number(wastage) || 0;
  const total = a + w;
  const planned = plannedEditable ? (parseInt(plannedText) || 0) : entry.plannedQty;
  const delta = total - planned;
  const match = delta === 0;
  const over = delta > 0;
  const under = delta < 0 && total > 0;

  const tone = match
    ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : over
      ? 'text-amber-700 bg-amber-50 border-amber-200'
      : under
        ? 'text-red-700 bg-red-50 border-red-200'
        : 'text-slate-500 bg-slate-50 border-slate-200';

  const disabled = a === 0 && w === 0;

  const commit = () => {
    if (disabled) return;
    onSave(a, w, planned);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    if (e.key === 'Escape') onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div
        className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-3">
          <h2 className="text-lg font-semibold text-brand">
            {entry.actualQty !== null ? 'Correct actuals' : 'Fill actuals'}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            <span className="font-mono">{entry.jobId.split('/').pop()}</span>
            {' · '}{entry.productName || entry.jobName}
            {' · '}{entry.client}
          </p>
        </div>

        <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 mb-4 text-sm flex items-center justify-between">
          <span className="text-slate-500 text-xs uppercase tracking-wide">Planned</span>
          {plannedEditable ? (
            <span className="flex items-center gap-1.5">
              <input type="number" inputMode="numeric" value={plannedText} onChange={e => setPlannedText(e.target.value)} onKeyDown={onKey}
                className="w-28 rounded border border-slate-300 px-2 py-1 text-base text-right font-semibold tabular-nums focus:border-brand focus:ring-1 focus:ring-brand" />
              <span className="text-xs text-slate-500">sheets</span>
            </span>
          ) : (
            <span className="font-semibold tabular-nums text-slate-800 text-base">
              {planned.toLocaleString()} <span className="text-xs font-normal text-slate-500">sheets</span>
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Actual sheets</label>
            <input
              type="number"
              inputMode="numeric"
              value={actual}
              onChange={e => setActual(e.target.value)}
              onKeyDown={onKey}
              autoFocus
              placeholder="0"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-lg text-right tabular-nums focus:border-brand focus:ring-1 focus:ring-brand"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Wastage sheets</label>
            <input
              type="number"
              inputMode="numeric"
              value={wastage}
              onChange={e => setWastage(e.target.value)}
              onKeyDown={onKey}
              placeholder="0"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-lg text-right tabular-nums focus:border-brand focus:ring-1 focus:ring-brand"
            />
          </div>
        </div>

        <div className={`rounded border px-3 py-2 mb-4 text-sm flex items-center justify-between ${tone}`}>
          <span>
            <span className="tabular-nums">{a.toLocaleString()}</span>
            {' + '}
            <span className="tabular-nums">{w.toLocaleString()}</span>
            {' = '}
            <span className="font-semibold tabular-nums">{total.toLocaleString()}</span>
          </span>
          <span className="text-xs font-semibold">
            {match ? '✓ matches plan'
              : over ? `+${delta.toLocaleString()} over`
              : under ? `${delta.toLocaleString()} short`
              : 'waiting…'}
          </span>
        </div>

        {under && (
          <p className="text-xs text-slate-500 mb-3">
            The remaining <b>{(planned - total).toLocaleString()}</b> sheets will move to tomorrow's plan, unless you Close on the next prompt.
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn-primary"
            disabled={disabled}
            onClick={commit}
          >Save</button>
        </div>
      </div>
    </div>
  );
}

/* ── BoardBadge ─────────────────────────────────────────────── */

function BoardBadge({ status, expectedDate }: { status: string; expectedDate: string | null }) {
  if (!status) return <span className="text-slate-400">—</span>;
  const color = status === 'Available' ? 'bg-green-100 text-green-700'
    : status === 'Not Available' ? 'bg-red-100 text-red-700'
    : 'bg-slate-100 text-slate-600';
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] ${color}`}>
      {status}{expectedDate ? ` (${fmtDate(expectedDate)})` : ''}
    </span>
  );
}
